import { useDocumentQuota } from "../../features/documents/hooks";

/**
 * Display document quota with progress bar and usage statistics
 * Shows warning when approaching or at limit
 */
export function DocumentQuotaDisplay() {
  const { quota, isLoading } = useDocumentQuota();

  if (isLoading || !quota) {
    return (
      <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Document Quota
        </h3>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Loading quota information...
        </div>
      </div>
    );
  }

  const { documents, plan } = quota;
  const usagePercentage = documents.percentUsed;
  const isNearLimit = usagePercentage >= 80;
  const isAtLimit = usagePercentage >= 100;

  // Progress bar color based on usage
  const getProgressBarColor = () => {
    if (isAtLimit) return "bg-red-600";
    if (isNearLimit) return "bg-yellow-600";
    return "bg-accent";
  };

  return (
    <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Document Quota
      </h3>

      {/* Usage stats */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {documents.current} /{" "}
            {documents.max > 0 ? documents.max : "Unlimited"} documents
          </span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {usagePercentage}%
          </span>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
          <div
            className={`${getProgressBarColor()} h-2.5 rounded-full transition-all duration-300`}
            style={{ width: `${Math.min(usagePercentage, 100)}%` }}
          />
        </div>
      </div>

      {/* Remaining count */}
      <div className="mb-3">
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {documents.remaining !== null
            ? `${documents.remaining} remaining`
            : "Unlimited"}
        </span>
      </div>

      {/* Plan name */}
      <div className="mb-4">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
          {plan.displayName} Plan
        </span>
      </div>

      {/* Warning messages */}
      {isAtLimit && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p className="text-sm text-red-800 dark:text-red-400 font-medium">
            Quota limit reached
          </p>
          <p className="text-xs text-red-600 dark:text-red-500 mt-1">
            You cannot upload more documents. Please upgrade your plan or
            contact support.
          </p>
        </div>
      )}

      {isNearLimit && !isAtLimit && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
          <p className="text-sm text-yellow-800 dark:text-yellow-400 font-medium">
            Approaching quota limit
          </p>
          <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-1">
            You are close to your document limit. Consider upgrading your plan.
          </p>
        </div>
      )}
    </div>
  );
}
